import React, { Fragment, Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as types from '../../constants/actionTypes';
import { actions as locationTaskActions } from '../../actions/locationTaskActions';
import { actions as filesActions } from '../../actions/filesActions';
import { actions as companyDashboardActions } from '../../actions/companyDashboardActions';
import { MOBILE_WINDOW_BREAKPOINT } from '../../constants/constants';
import DashboardWidget from '../Common/DashboardWidget/DashboardWidget';
import DashboardMidgetWidget from '../Common/DashboardMidgetWidget/DashboardMidgetWidget';
import MissedMilestones from './Widgets/MissedMilestones';
import UpcomingTasks from './Widgets/UpcomingTasks';
import CustomizeWidgetsDrawer from './CustomizeWidgetsDrawer';
import CorporateDashboardHeader from './CorporateDashboardHeader';
import LocationSummary from './Widgets/LocationSummary';
import Workload from './Widgets/Workload';
import './CorporateDashboardPage.scss';
import Projects from './Widgets/Projects';
import TaskDetailManager from '../TaskList/TaskDetail/TaskDetailManager';
import LoadingIndicator from '../Common/LoadingIndicator/LoadingIndicator';
import Notification from '../Common/Notification';
import ManageCorporateViews from './ManageCorporateViews';
import { withAbortableApi } from '../Common/AbortableApi';
import LocationTaskSidebar from '@Components/TaskList/TaskDetail/LocationTaskSidebar/LocationTaskSidebar';

class CorporateDashboard extends Component {
	constructor(props, context) {
		super(props, context);

		const defaultSettings = {
			updates: 0, //this is used to track state changes from customize widget drawer so child components can know when to update without a deep compare
			isProjectSummariesActive: true,
			isLocationSummariesActive: true,
			isMyTasksDueSoonActive: true,
			isMilestonesMissedActive: true,
			isNameSearch: true,
			brandIds: [],
			areaIds: [],
			regionIds: [],
			managementCompanyIds: [],
			locationTypeIds: [],
			continentIds: [],
			isMilestonesOnly: false,
			disciplineIds: [],
			departmentIds: [],
			phaseIds: [],
			communityIds: [],
			baselineIds: [],
			projectIds: [],
			locationIds: [],
			tagIds: [],
		};

		this.state = {
			isLoadingFilters: true,
			isLoadingSettings: true,
			editLocationKey: '',
			editTaskId: '',
			isTaskDetailOpen: false,
			isSubscriptionProjectWidgetActive: false,
			accessibleLocationCount: 0,
			showCustomizerDrawer: false,
			locationUsers: [],
			lastItemIdDeleted: '',
			filters: {
				// location filters
				brands: [],
				areas: [],
				regions: [],
				managementCompanies: [],
				locationTypes: [],
				continents: [],
				//task filters
				disciplines: [],
				departments: [],
				phases: [],
				communities: [],
				baselines: [],
				projects: [],
				tags: [],
			},
			settings: [],
			selectedSettingsId: 'defaultId',
			defaultSettings: defaultSettings,
			selectedSettings: defaultSettings,
			matchingSettings: [],
			showManageCorporateViews: false,
		};
	}

	isMobile = () => {
		return this.props.windowWidth < MOBILE_WINDOW_BREAKPOINT;
	};

	componentDidMount = async () => {
		await this.loadFiltersV2();
		await this.loadSettingsV2();
	};

	componentDidUpdate = async (prevProps) => {
		const { user } = this.props;

		if (user.currentSubscriptionId && user.currentSubscriptionId !== prevProps.user.currentSubscriptionId) {
			await Promise.all([this.loadFiltersV2(), this.loadSettingsV2()]);
		}
	};

	/**
	 * @deprecated This function is deprecated, all filters will be moved to MongoDB
	 */
	loadFilters = async () => {
		this.setState({ isLoadingFilters: true });
		try {
			const response = await this.props.companyDashboardActions.loadDashboardFilters();
			if (response.type === types.LOAD_COMPANY_DASHBOARD_FILTERS_SUCCESS) {
				const newSettings = response.payload.settings ? response.payload.settings : this.state.defaultSettings;
				this.setState({
					isSubscriptionProjectWidgetActive: response.payload.isSubscriptionProjectWidgetActive,
					accessibleLocationCount: response.payload.accessibleLocationCount,
					filters: { ...this.state.filters, ...response.payload.filters },
					settings: { ...this.state.settings, ...newSettings },
				});
			} else if (response.type === types.LOAD_COMPANY_DASHBOARD_FILTERS_FAILURE) {
				Notification('error', 'Error', response.payload.message);
			}
		} catch (error) {
			Notification('error', 'Error', error);
		} finally {
			this.setState({ isLoadingFilters: false });
		}
	};

	loadFiltersV2 = async () => {
		this.setState({ isLoadingFilters: true });
		try {
			const response = await this.props.callAbortableApi((signal) => this.props.companyDashboardActions.loadDashboardFiltersV2(signal));

			if (response.type === types.LOAD_COMPANY_DASHBOARD_FILTERS_SUCCESS) {
				this.setState({
					isSubscriptionProjectWidgetActive: response.payload.isSubscriptionProjectWidgetActive,
					accessibleLocationCount: response.payload.accessibleLocationCount,
					filters: { ...this.state.filters, ...response.payload.filters },
				});
			} else if (response.type === types.LOAD_COMPANY_DASHBOARD_FILTERS_FAILURE) {
				Notification('error', 'Error', response.payload.message);
			}
		} catch (error) {
			Notification('error', 'Error', error);
		} finally {
			this.setState({ isLoadingFilters: false });
		}
	};

	loadSettingsV2 = async () => {
		this.setState({ isLoadingSettings: true });
		try {
			const response = await this.props.callAbortableApi((signal) => this.props.companyDashboardActions.loadDashboardSettingsV2(signal));
			if (response.type === types.LOAD_COMPANY_DASHBOARD_SETTINGS_SUCCESS && response.payload) {
				this.setState(
					(prevState) => {
						const newSelectedSettingsId =
							prevState.selectedSettingsId === 'defaultId' && response.payload[0].id
								? response.payload[0].id
								: prevState.selectedSettingsId;
						const newSettings = response.payload?.[0] ?? this.state.defaultSettings;

						return {
							settings: response.payload,
							selectedSettingsId: newSelectedSettingsId,
							selectedSettings: newSettings,
							showCustomizerDrawer: false,
						};
					},
					() => this.onSettingsMenuChange(this.state.selectedSettingsId)
				);
			} else if (response.type === types.LOAD_COMPANY_DASHBOARD_SETTINGS_FAILURE) {
				Notification('error', 'Error', response.payload.message);
			}
		} catch (error) {
			Notification('error', 'Error', error);
		} finally {
			this.setState({ isLoadingSettings: false });
		}
	};

	getResponsibleUsersForLocations = async (locationKey) => {
		try {
			const response = await this.props.callAbortableApi((signal) =>
				this.props.locationTaskActions.searchResponsibleUsers([locationKey], undefined, signal)
			);
			if (response.type === types.SEARCH_LOCATION_TASK_RESPONSIBLE_USERS_SUCCESS) {
				this.setState({ locationUsers: response.payload.users });
			} else if (response.type === types.SEARCH_LOCATION_TASK_RESPONSIBLE_USERS_FAILURE) {
				Notification('error', 'Error', response.payload.message);
			}
		} catch (error) {
			Notification('error', 'Error', error);
		}
	};

	showTask = async (taskId, locationKey) => {
		await this.getResponsibleUsersForLocations(locationKey);

		this.setState({
			isTaskDetailOpen: true,
			editTaskId: taskId,
			editLocationKey: locationKey,
		});
	};

	onTaskDetailComplete = () => {
		this.setState({ isTaskDetailOpen: false }, async () => {
			await this.loadFiltersV2();
			await this.loadSettingsV2();
		});
	};

	onTaskDetailCancel = () => {
		this.setState({ isTaskDetailOpen: false });
	};

	openCustomizerDrawer = () => {
		this.setState({ showCustomizerDrawer: true });
	};

	onCompleteCustomizerDrawer = (newSettings) => {
		this.setState({ selectedSettings: newSettings, selectedSettingsId: newSettings.id, showCustomizerDrawer: false });
	};

	onCloseCustomizerDrawer = () => {
		this.setState({ showCustomizerDrawer: false });
	};

	// Sets the filter to a single selected project
	onFilterByProject = (projectId) => {
		const updatedSettings = {
			...this.state.selectedSettings,
			projectIds: [projectId],
			updates: this.state.selectedSettings.updates + 1,
		};

		this.setState({ selectedSettings: updatedSettings });
	};

	checkAvailability = async (settings) => {
		try {
			const response = await this.props.callAbortableApi((signal) =>
				this.props.companyDashboardActions.checkSettingsAvailabilityV2(settings, signal)
			);

			if (response.type === types.CHECK_COMPANY_DASHBOARD_SETTINGS_SUCCESS) {
				this.setState({ matchingSettings: response.payload });
			} else if (response.type === types.CHECK_COMPANY_DASHBOARD_SETTINGS_FAILURE) {
				Notification('error', 'Error', response.payload.message);
			}
		} catch (error) {
			Notification('error', 'Error', error);
			this.setState({ isLoading: false });
		}
	};

	onSettingsMenuChange = (selectedSettingsId) => {
		let newSettings =
			this.state.settings.find((setting) => setting?.id === selectedSettingsId) ??
			// If no setting is found, set default settings
			this.state.defaultSettings;

		this.setState({
			// settings: [...this.state.settings, newSettings],
			selectedSettings: newSettings,
			selectedSettingsId: selectedSettingsId,
			showCustomizerDrawer: false,
		});
	};

	toggleActiveState = (newState) => {
		this.setState({ showManageCorporateViews: newState });
	};

	setLastViewDeleted = async (newState) => {
		await this.loadSettingsV2();

		if (this.state.lastItemIdDeleted === newState) {
			this.setState({ showManageCorporateViews: false });
			return;
		}

		this.setState({ lastItemIdDeleted: newState, showManageCorporateViews: false });
	};

	cleanLastViewDeleted = () => {
		if (!this.state.lastItemIdDeleted) return;

		this.setState({ lastItemIdDeleted: '' });
	};

	render() {
		return (
			<Fragment>
				{this.state.showManageCorporateViews ? (
					<ManageCorporateViews setLastViewDeleted={this.setLastViewDeleted}></ManageCorporateViews>
				) : (
					<LoadingIndicator
						isLoading={this.state.isLoadingFilters || this.state.isLoadingSettings}
						isFullscreen={true}
						hideWhileLoading={true}>
						<div className='corporate-dashboard'>
							{!this.state.isLoadingSettings && (
								<Fragment>
									{this.state.showCustomizerDrawer && (
										<CustomizeWidgetsDrawer
											onComplete={this.onCompleteCustomizerDrawer}
											onClose={this.onCloseCustomizerDrawer}
											settings={this.state.selectedSettings}
											defaultSettings={this.state.defaultSettings}
											filters={this.state.filters}
											isSubscriptionProjectWidgetActive={this.state.isSubscriptionProjectWidgetActive}
											matchingSettings={this.state.matchingSettings}
											checkAvailability={this.checkAvailability}
										/>
									)}

									{this.state.settings && !this.state.showManageCorporateViews && (
										<DashboardWidget bgColor='white'>
											<CorporateDashboardHeader
												openCustomizerDrawer={this.openCustomizerDrawer}
												onSettingsMenuChange={this.onSettingsMenuChange}
												reloadSettings={this.loadSettingsV2}
												settings={this.state.settings}
												selectedSettings={this.state.selectedSettings}
												selectedSettingsId={this.state.selectedSettingsId}
												companyDashboardActions={this.props.companyDashboardActions}
												matchingSettings={this.state.matchingSettings}
												toggleActiveState={this.toggleActiveState}
												lastItemIdDeleted={this.state.lastItemIdDeleted}
												cleanLastViewDeleted={this.cleanLastViewDeleted}
											/>
										</DashboardWidget>
									)}
									{this.state.selectedSettings.isProjectSummariesActive && this.state.isSubscriptionProjectWidgetActive && (
										<Fragment>
											<DashboardWidget bgColor='gray'>
												<Projects settings={this.state.selectedSettings} onFilterByProject={this.onFilterByProject} />
											</DashboardWidget>
											<DashboardWidget bgColor='gray'></DashboardWidget>
										</Fragment>
									)}

									<DashboardWidget bgColor='white'>
										<Workload
											settings={this.state.selectedSettings}
											filters={this.state.filters}
											accessibleLocationCount={this.state.accessibleLocationCount}
										/>
									</DashboardWidget>

									{this.state.selectedSettings.isLocationSummariesActive && (
										<DashboardWidget bgColor='gray'>
											<LocationSummary settings={this.state.selectedSettings} />
										</DashboardWidget>
									)}

									<DashboardWidget bgColor='white'>
										<div className='midget-container row g-3'>
											{this.state.selectedSettings.isMyTasksDueSoonActive && (
												<DashboardMidgetWidget>
													<div className='row'>
														<h5 className='dashboard-widget-title'>My Tasks</h5>
													</div>
													<UpcomingTasks
														onSelectTask={this.showTask}
														settings={this.state.selectedSettings}
														companyDashboardActions={this.props.companyDashboardActions}
													/>
												</DashboardMidgetWidget>
											)}
											{this.state.selectedSettings.isMilestonesMissedActive && (
												<DashboardMidgetWidget>
													<div className='row'>
														<h5 className='dashboard-widget-title'>Past Due Milestones</h5>
													</div>
													<MissedMilestones
														onSelectTask={this.showTask}
														settings={this.state.selectedSettings}
														companyDashboardActions={this.props.companyDashboardActions}
													/>
												</DashboardMidgetWidget>
											)}
										</div>
									</DashboardWidget>

									{this.state.isTaskDetailOpen && (
										<LocationTaskSidebar
											locationTaskId={this.state.editTaskId}
											changeTask={(editTaskId) => this.setState({ editTaskId })}
											locationKey={this.state.editLocationKey}
											isDrawerVisible={this.state.isTaskDetailOpen}
											responsibleUsers={this.state.locationUsers}
											closeDrawer={this.onTaskDetailCancel}
											onComplete={this.onTaskDetailComplete}
											isMobile={this.isMobile()}
										/>
									)}
								</Fragment>
							)}
						</div>
					</LoadingIndicator>
				)}
			</Fragment>
		);
	}
}

CorporateDashboard.propTypes = {
	windowWidth: PropTypes.number,
	companyDashboardActions: PropTypes.object.isRequired,
	filesActions: PropTypes.object.isRequired,
	locationTaskActions: PropTypes.object.isRequired,
	user: PropTypes.object,
	history: PropTypes.object.isRequired,
	callAbortableApi: PropTypes.func.isRequired,
};

function mapStateToProps(state) {
	return {
		windowWidth: state.window.width,
		user: state.user,
	};
}

function mapDispatchToProps(dispatch) {
	return {
		companyDashboardActions: bindActionCreators(companyDashboardActions, dispatch),
		locationTaskActions: bindActionCreators(locationTaskActions, dispatch),
		filesActions: bindActionCreators(filesActions, dispatch),
	};
}

export default connect(mapStateToProps, mapDispatchToProps)(withAbortableApi(CorporateDashboard));
